import { Icon } from '@gftg/ui';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Avatar } from '../components/Avatar';
import { CategoryChip, CertificationChip } from '../components/Chips';
import { LinkedInButton } from '../components/LinkedInButton';
import { ErrorBlock, ICON_BUTTON, LoadingBlock } from '../components/states';
import { roleLine } from '../lib/people';
import { usePerson } from '../lib/queries';

/**
 * One attendee, reached from the directory or from a match (DIR-05).
 *
 * The design's contact card is not here. It shows an email address and a phone number, and PRF-05
 * forbids both on this screen at any setting — the questionnaire tells the attendee outright that
 * no setting can expose them, so printing them would break a promise the app made to the person
 * whose data it is. Contact exchange through an accepted connection is what would have unlocked
 * them, and the client's Aug 19 decision took that out of R1. Until it returns, the card explains
 * itself rather than being silently dropped, and LinkedIn stays: it is the one link the
 * questionnaire lists as visible to other attendees.
 *
 * There is no "request a connection" for the same reason — introductions come from Concierge
 * Connections, which pairs attendees on their questionnaire answers rather than on asking.
 */
export function PersonPage() {
  const { id = '' } = useParams();
  const person = usePerson(id);

  if (person.isPending) {
    return <LoadingBlock label="Loading the profile…" />;
  }

  if (person.isError || !person.data) {
    return <ErrorBlock message="That attendee is not in the directory." />;
  }

  const data = person.data;
  const role = roleLine(data.jobTitle, data.company);

  // Two vocabularies, one question. Goals are what they came for and interests are the subjects
  // they picked, and the design puts both in a single row under one heading — which is what an
  // attendee reads them as. The API keeps them apart, so a later screen can still tell them apart.
  const hereFor = [...data.goals, ...data.interests];

  return (
    <>
      <header className="px-gutter flex items-center gap-3 border-b border-border-2 bg-surface py-3">
        <BackButton />
        <p className="text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase">Attendee</p>
      </header>

      {/* Who she is, then what there is to do about it. On a phone that is one column in that
          order; on a laptop the first half becomes a rail on the left, so what she came for is
          beside her name rather than a scroll below it. */}
      <main className="px-gutter pt-6 pb-6 lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-10">
        <div>
          <Avatar name={data.name} photoAssetId={data.photoAssetId} size="profile" />

          <h1 className="font-display text-session-title mt-5 font-semibold text-ink">
            {data.name}
          </h1>
          {role && <p className="text-row mt-2 text-ink-2">{role}</p>}
          {data.industry && <p className="text-row mt-1 text-ink-3">{data.industry}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            <CategoryChip label={data.category} size="profile" />
            {data.certifications.map((certification) => (
              <CertificationChip
                key={certification.id}
                label={certification.label}
                size="profile"
              />
            ))}
          </div>
        </div>

        <div>
          {hereFor.length > 0 && (
            <section className="mt-6">
              <h2 className="text-eyebrow tracking-eyebrow mb-2 font-bold text-ink-3 uppercase">
                Here for
              </h2>
              <ul className="flex flex-wrap gap-2">
                {hereFor.map((tag) => (
                  <li
                    key={tag.id}
                    className="text-subtitle rounded-full border border-border bg-surface px-4 py-2 text-ink-2"
                  >
                    {tag.label}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="rounded-card mt-6 border border-border bg-surface p-4">
            <h2 className="text-eyebrow tracking-eyebrow mb-2 font-bold text-ink-3 uppercase">
              Contact
            </h2>
            <p className="text-body text-ink-3">
              Email and phone numbers are never shown to other attendees. Reach out on LinkedIn, or
              ask the organizers for an introduction.
            </p>
          </div>

          {data.linkedinUrl && (
            // Full width on a phone, where it is the screen's one action. On a laptop a button
            // 600px wide for one word is a target nobody can miss and nobody can read either.
            <div className="mt-4 lg:max-w-xs">
              <LinkedInButton url={data.linkedinUrl} />
            </div>
          )}

          {/* DIR-04 again: they are listed on their registration answers, and saying so is kinder
            than a profile that looks unfinished for no stated reason. */}
          {!data.hasProfile && (
            <p className="text-body mt-6 text-ink-3">
              {data.name.split(' ').at(0)} has not filled in a profile yet. Everything here comes
              from the registration answers.
            </p>
          )}
        </div>
      </main>
    </>
  );
}

/**
 * This screen is reached from two lists, so "back" is history rather than a fixed destination —
 * except when the URL was opened directly, where there is no history to go back to.
 */
function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  if (location.key === 'default') {
    return (
      <Link to="/directory" aria-label="Who’s coming" className={ICON_BUTTON}>
        <Icon name="chevron-left" size={18} />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void navigate(-1)}
      aria-label="Back"
      className={ICON_BUTTON}
    >
      <Icon name="chevron-left" size={18} />
    </button>
  );
}
